/**
 * Verkauf von Restaurant-Gerichten — nur Umsatz, kein Stock-Abzug.
 */

import { generateVenteId } from '../../utils/frenchIdGenerator';
import { computeSaleActiveTotal } from '../sales/saleLineMath';
import { isRestaurantDrinkMenuSection } from '../../utils/restaurantMenuCatalog';

export const RESTAURANT_SALE_TYPE = 'restaurant_dish';

export function isRestaurantDishSale(sale) {
  return sale?.saleType === RESTAURANT_SALE_TYPE;
}

export function filterRestaurantDishSales(sales) {
  return (sales || []).filter(isRestaurantDishSale);
}

/**
 * Canonical per-sale money amount (retail + restaurant dishes).
 * Fast path: stored totals when no void metadata.
 * Void / full cancel → active line sum or 0.
 */
export function getSaleTotalAmount(sale) {
  if (!sale) return 0;

  // Cheap status cancel — avoid walking product lines on the common path.
  const status = String(sale?.status || '').toLowerCase();
  if (status === 'cancelled' || status === 'voided' || sale?.voided === true) return 0;

  const products = Array.isArray(sale.products) ? sale.products : [];
  if (products.length > 0) {
    let hasVoidMeta = false;
    for (let i = 0; i < products.length; i += 1) {
      if (Number(products[i]?.voidedQuantity) > 0) {
        hasVoidMeta = true;
        break;
      }
    }
    if (hasVoidMeta) {
      return computeSaleActiveTotal(sale);
    }
  }

  if (isRestaurantDishSale(sale) && Array.isArray(sale.dishes)) {
    return sale.dishes.reduce(
      (sum, dish) =>
        sum +
        Number(
          dish.lineTotal != null
            ? dish.lineTotal
            : Number(dish.quantity) * Number(dish.unitPrice)
        ),
      0
    );
  }

  const direct = Number(sale?.total ?? sale?.totalAmount ?? sale?.amount ?? sale?.revenue ?? 0);
  if (Number.isFinite(direct) && direct > 0) return direct;

  // Stored total missing — derive from priced lines (rare / legacy).
  if (products.length > 0) {
    const active = computeSaleActiveTotal(sale);
    if (active > 0) return active;
  }

  return 0;
}

export function getRestaurantDishSaleQuantity(sale) {
  if (!isRestaurantDishSale(sale)) return 0;
  return (sale.dishes || []).reduce((sum, dish) => sum + (Number(dish.quantity) || 0), 0);
}

export function summarizeRestaurantDishSales(sales) {
  const dishSales = filterRestaurantDishSales(sales);
  let totalQuantity = 0;
  let drinksQuantity = 0;
  let foodQuantity = 0;
  const byName = {};

  for (const sale of dishSales) {
    for (const dish of sale.dishes || []) {
      const qty = Number(dish.quantity) || 0;
      totalQuantity += qty;
      const section = dish.menuSection || dish.subcategory || 'other';
      if (isRestaurantDrinkMenuSection(section)) {
        drinksQuantity += qty;
      } else {
        foodQuantity += qty;
      }
      const name = dish.dishName || '—';
      byName[name] = (byName[name] || 0) + qty;
    }
  }

  const topSold = Object.entries(byName)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([name, quantity]) => ({ name, quantity }));

  return {
    transactionCount: dishSales.length,
    totalQuantity,
    drinksQuantity,
    foodQuantity,
    topSold,
    revenue: dishSales.reduce((sum, sale) => sum + getSaleTotalAmount(sale), 0)
  };
}

export function summarizeSalesByType(sales) {
  const list = sales || [];
  const dishDetail = summarizeRestaurantDishSales(list);
  let retailRevenue = 0;
  let retailCount = 0;

  for (const sale of list) {
    if (isRestaurantDishSale(sale)) continue;
    retailRevenue += getSaleTotalAmount(sale);
    retailCount += 1;
  }

  return {
    restaurantDishRevenue: dishDetail.revenue,
    restaurantDishCount: dishDetail.transactionCount,
    restaurantDishQuantity: dishDetail.totalQuantity,
    restaurantDrinksQuantity: dishDetail.drinksQuantity,
    restaurantFoodQuantity: dishDetail.foodQuantity,
    restaurantTopSold: dishDetail.topSold,
    retailRevenue,
    retailCount,
    totalRevenue: dishDetail.revenue + retailRevenue
  };
}

/**
 * @param {object} input
 * @param {string} input.orderId
 * @param {string} [input.date]
 * @param {string} [input.paymentMethod]
 * @param {string} [input.note]
 * @param {{ dishId: string, dishName: string, quantity: number, unitPrice: number }[]} input.dishes
 */
export function buildRestaurantDishSale(input) {
  const lines = (input.dishes || []).map((d) => {
    const qty = Number(d.quantity) || 0;
    const unitPrice = Number(d.unitPrice) || 0;
    return {
      dishId: d.dishId,
      dishName: d.dishName,
      quantity: qty,
      unitPrice,
      lineTotal: qty * unitPrice,
      menuSection: d.menuSection || d.subcategory || null,
      subcategory: d.subcategory || d.menuSection || null,
      type: d.type || null
    };
  });
  const total = lines.reduce((sum, l) => sum + l.lineTotal, 0);
  const orderId = input.orderId;

  return {
    id: orderId,
    orderId,
    date: input.date || new Date().toISOString().split('T')[0],
    saleType: RESTAURANT_SALE_TYPE,
    paymentMethod: input.paymentMethod || 'cash',
    dishes: lines,
    products: [],
    total,
    totalAmount: total,
    status: 'completed',
    note: (input.note || '').trim(),
    createdAt: new Date().toISOString()
  };
}

export async function createRestaurantDishSale(businessId, input) {
  if (!input?.dishes?.length) {
    return { success: false, error: 'Ajoutez au moins un plat' };
  }
  const invalid = input.dishes.some(
    (d) => !d.dishId || !Number(d.quantity) || Number(d.quantity) <= 0
  );
  if (invalid) {
    return { success: false, error: 'Plat et quantité valides requis' };
  }

  const orderId = await generateVenteId(businessId);
  const sale = buildRestaurantDishSale({ ...input, orderId });
  return { success: true, sale };
}
